import {useCallback} from 'react';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {useConfirmation} from '@netx/core-components/Confirmation';
import {ASSET_EXPIRATION_EVENT} from '../../actions';
import {expireAsset} from '../../api';
import {userInputToExpirationTime} from '../../utils';
import {
	ASSET_EXPIRATION_CONFIRMATIONS,
	ASSET_EXPIRATION_SUBMIT_LABELS,
} from '../../locales';
import {useAssetExpirationPubSub, PUB_SUB_ACTION} from './pubsub';

export const useAssetExpiration = () => {
	const confirm = useConfirmation();

	const {performAction, emit, subscribe, working} = useAssetExpirationPubSub();

	useDispatcher('asset:expire:success', (model, expirationDate) => {
		emit({
			action: 'EXPIRATION_SET',
			payload: {
				id: model.id,
				expirationDate,
			},
		});
	});

	useDispatcher('asset:unexpire:success', (model) => {
		emit({
			action: 'EXPIRATION_UNSET',
			payload: {
				id: model.id,
				expirationDate: null,
			},
		});
	});

	const expire = useCallback(
		async (id: number, expirationDate: number) =>
			await performAction(PUB_SUB_ACTION.EXPIRATION_SET, async () => {
				await expireAsset({id, expirationDate});
				dispatcher.trigger(
					`${ASSET_EXPIRATION_EVENT.EXPIRE}:success`,
					id,
					expirationDate,
				);
				return {
					id,
					expirationDate,
				};
			}),
		[],
	);

	const expireWithDate = useCallback(
		async (id: number, dateStr: string) =>
			await expire(id, userInputToExpirationTime(dateStr)),
		[],
	);

	const expireWithMilliseconds = useCallback(
		async (id: number, milliseconds: number) => await expire(id, milliseconds),
		[],
	);

	const unexpire = useCallback(async (id: number) => {
		return confirm({
			message: ASSET_EXPIRATION_CONFIRMATIONS.UNEXPIRE,
			confirmLabel: ASSET_EXPIRATION_SUBMIT_LABELS.UNEXPIRE,
		}).then(() => {
			return performAction(PUB_SUB_ACTION.EXPIRATION_UNSET, async () => {
				await expireAsset({id, expirationDate: 0});
				dispatcher.trigger(`${ASSET_EXPIRATION_EVENT.UNEXPIRE}:success`, id, 0);
				return {
					id,
					expirationDate: null,
				};
			});
		});
	}, []);

	return {
		expireWithDate,
		expireWithMilliseconds,
		unexpire,
		subscribe,
		working,
	};
};

export default useAssetExpiration;
